import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { trackFeatureAction } from '../../../analytics/googleAnalytics';
import * as XLSX from 'xlsx';
import jwt_decode from 'jwt-decode';
import { saveAs } from 'file-saver';
import {
  Upload,
  message,
  Table,
  Card,
  Button,
  Typography,
  Spin,
  Drawer,
  Descriptions,
  Tag,
  Space,
  Divider,
  Tooltip,
  Badge,
  Avatar,
  Form,
  Input,
  InputNumber,
  Select,
  Row,
  Col
} from 'antd';
import {
  InboxOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  EyeOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  PhoneOutlined,
  MailOutlined,
  HomeOutlined,
  BankOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  UserOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import {
  uploadDigitalLeadFile,
  fetchDigitalLeads,
  fetchLeadDetails,
  resetUploadStatus,
  clearCurrentLead,
  updateLeadDetails
} from '../../../redux/slice/digitalLeads';
import moment from 'moment';

const { Title, Text } = Typography;
const { Dragger } = Upload;
const { Option } = Select;
const { TextArea } = Input;

// Styled components
const DigitalLeadsContainer = styled.div`
  padding: 24px;
`;

const UploadSection = styled(Card)`
  margin-bottom: 24px;
  .ant-upload.ant-upload-drag {
    padding: 40px;
  }
`;

const StatusTag = styled(Tag)`
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 12px;
`;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const DetailSection = styled.div`
  margin-bottom: 24px;
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  
  h4 {
    margin: 0;
    font-size: 16px;
    font-weight: 500;
  }
  
  .anticon {
    margin-right: 8px;
    color: #1890ff;
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  
  .anticon {
    margin-right: 8px;
    color: rgba(0, 0, 0, 0.45);
  }
`;

const EditFormContainer = styled.div`
  .ant-form-item {
    margin-bottom: 12px;
  }
`;

const DigitalLeadsScreen = ({ jwtBearer, branchData, props }) => {
  console.log('Branch Data Digital:', jwtBearer);
  var jwt_payload = "";
  var jwt_payload_group = [];
  var user_id = "";
  var user_role = ""
  var user_name = ""
  if (jwtBearer) {
    jwt_payload = jwt_decode(jwtBearer);
    user_name = props.userDetails?.username;
    user_id = jwt_payload.user_id;
    user_role = props.userDetails?.role;
    jwt_payload_group = props.userDetails?.group_id;
  }
  const dispatch = useDispatch();

  const {
    leads,
    currentLead,
    meta,
    loading,
    error,
    uploadStatus,
    lastUploadTime
  } = useSelector((state) => state.digitalLeads);

  const [fileList, setFileList] = useState([]);
  const [isDetailsDrawerVisible, setIsDetailsDrawerVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [branchToAssign, setBranchToAssign] = useState('')
  const [bMToAssign, setBMToAssign] = useState("")
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  useEffect(() => {
    setBMToAssign(branchData[branchToAssign]?.bmEmpCode || '');
  }, [branchToAssign, branchData]);

  // Fetch leads on mount and when pagination changes
  useEffect(() => {
    dispatch(fetchDigitalLeads({
      userid: user_id,
      userRole: user_role,
      page: pagination.current,
      limit: pagination.pageSize,
      jwtBearer
    }));
  }, [dispatch, jwtBearer, pagination]);

  // Handle upload status changes
  useEffect(() => {
    if (uploadStatus === 'succeeded') {
      trackFeatureAction('DigitalJourney', "Digital Leads", 'upload');
      message.success('File uploaded successfully!');
      setFileList([]);
      dispatch(resetUploadStatus());
      // Refresh leads after successful upload
      dispatch(fetchDigitalLeads({
        page: pagination.current,
        limit: pagination.pageSize,
        jwtBearer
      }));
    } else if (uploadStatus === 'failed') {
      message.error(error || 'File upload failed');
    }
  }, [uploadStatus, error, dispatch, jwtBearer, pagination]);

  // Upload configuration
  const uploadProps = {
    name: 'file',
    fileList,
    multiple: false,
    accept: '.xlsx,.xls,.csv',
    beforeUpload: (file) => {
      const isAcceptableFormat = ['xlsx', 'xls', 'csv'].some(format =>
        file.name.toLowerCase().endsWith(format)
      );
      if (!isAcceptableFormat) {
        message.error('You can only upload Excel/CSV files!');
        return Upload.LIST_IGNORE;
      }
      setFileList([file]);
      return false; // Prevent automatic upload
    },
    onRemove: () => {
      setFileList([]);
    }
  };

  // Handle manual upload
  const handleUpload = () => {
    if (fileList.length === 0) {
      message.warning('Please select a file first');
      return;
    }

    dispatch(uploadDigitalLeadFile({
      file: fileList[0],
      jwtBearer
    }));
  };

  // Handle table pagination
  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  // View lead details
  const handleViewDetails = (objectId) => {
    trackFeatureAction(
      "DigitalJourney",
      "Digital Leads",
      "view"
    );
    dispatch(fetchLeadDetails({ objectId, jwtBearer }));
    setIsDetailsDrawerVisible(true);
    setIsEditing(false);
  };

  // Close details drawer
  const handleCloseDetails = () => {
    setIsDetailsDrawerVisible(false);
    dispatch(clearCurrentLead());
    setIsEditing(false);
  };

 const handleUpdateLead = async () => {
  try {
    const values = await form.validateFields();

    // Convert risky fields to strings
    const transformedValues = {
      ...values,
      phone_number: String(values.phone_number ?? ''),
      correct_pincode: String(values.correct_pincode ?? ''),
      alternative_number: String(values.alternative_number ?? ''),
      branch_remarks: String(values.branch_remarks ?? ''),
      loan_amount_required: String(values.loan_amount_required ?? ''),
      monthly_income: String(values.monthly_income ?? ''),
    };

    const response = await dispatch(
      updateLeadDetails({
        objectId: currentLead._id,
        updateData: transformedValues,
        jwtBearer,
      })
    ).unwrap();

    trackFeatureAction(
      "DigitalJourney",
      "Digital Leads",
      "save"
    );

    // FIX: Proper object passed to dispatch
    dispatch(fetchLeadDetails({ objectId: currentLead._id, jwtBearer }));

    message.success('Lead updated successfully');
    setIsEditing(false);
  } catch (error) {
    console.error('Update lead error:', error);
    message.error(error?.message || 'Failed to update lead');
  }
};



  const REQUIRED_HEADERS = [
    "Platform", "Full name", "City", "E-mail", "Phone_Number"
  ];

  const handleDownloadTemplate = () => {
    const worksheet = XLSX.utils.aoa_to_sheet([REQUIRED_HEADERS]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
    trackFeatureAction(
      "DigitalJourney",
      "Digital Leads",
      "download_template"
    );
    saveAs(data, 'template.xlsx');
  };

  // Refresh leads
  const handleRefresh = () => {
    trackFeatureAction(
      "DigitalJourney",
      "Digital Leads",
      "refresh"
    );

    dispatch(
      fetchDigitalLeads({
        userid: user_id,
        userRole: user_role,
        page: pagination.current,
        limit: pagination.pageSize,
        jwtBearer,
      })
    );
  };

  // Columns for the digital leads table
  const columns = [
    {
      title: 'Full Name',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (text, record) => (
        <Button type="link" onClick={() => handleViewDetails(record._id)}>
          {text}
        </Button>
      )
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          <div>{record.phone_number || 'N/A'}</div>
          <Text type="secondary">{record.e_mail || 'No email'}</Text>
        </div>
      )
    },
    {
      title: 'Location',
      key: 'location',
      render: (_, record) => (
        <div>
          <div>{record.city || 'N/A'}</div>
          <Text type="secondary">{record.district}, {record.state}</Text>
        </div>
      )
    },
    {
      title: 'Loan Details',
      key: 'loan',
      render: (_, record) => (
        <div>
          <div><strong>{record.loan_amount_required || 'N/A'}</strong></div>
          <Text type="secondary">{record.employment_type}</Text>
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'disposition',
      key: 'disposition',
      filters: [
        { text: 'Interested', value: 'Interested' },
        { text: 'Not Interested', value: 'Not Interested' },
        { text: 'Follow Up', value: 'Follow Up' },
      ],
      onFilter: (value, record) => record.disposition === value,
      render: (status) => {
        let color = 'default';
        if (status === 'Interested') color = 'green';
        if (status === 'Not Interested') color = 'red';

        return (
          <StatusTag color={color}>
            {status || 'Pending'}
          </StatusTag>
        );
      },
    },
    {
      title: 'Uploaded',
      dataIndex: 'uploaded_at',
      key: 'uploaded_at',
      render: (date) => moment(date).format('DD/MM/YY'),
      sorter: (a, b) => new Date(a.uploaded_at) - new Date(b.uploaded_at),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Tooltip title="View details">
          <Button
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record._id)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <DigitalLeadsContainer>
      <Title level={3} style={{ marginBottom: 24 }}>
        Digital Leads Management
      </Title>

   {user_role === 'Telecaller-Executive' ? null :  (<UploadSection title="Upload New Leads">
        <Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag file to this area to upload</p>
          <p className="ant-upload-hint">
            Supported formats: Excel (.xlsx, .xls) or CSV files. Maximum file size: 10MB.
          </p>
        </Dragger>

        <Space style={{ marginTop: 16 }}>
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            onClick={handleUpload}
            loading={uploadStatus === 'loading'}
            disabled={fileList.length === 0}
          >
            Upload File
          </Button>

          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownloadTemplate}
          >
            Download Template
          </Button>

          {lastUploadTime && (
            <Text type="secondary">
              Last uploaded: {moment(lastUploadTime).fromNow()}
            </Text>
          )}
        </Space>
      </UploadSection>)}

      <Card>
        <TableHeader>
          <div>
            <Title level={5} style={{ margin: 0 }}>
              Digital Leads
            </Title>
            <Text type="secondary">
              Showing {leads.length} of {meta.total_count} records
            </Text>
          </div>
          <Space>
            <Tooltip title="Refresh data">
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                loading={loading}
              />
            </Tooltip>
          </Space>
        </TableHeader>

        <Table
          columns={columns}
          dataSource={leads.map(lead => ({ ...lead, key: lead._id }))}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: meta.total_count,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
          }}
          onChange={handleTableChange}
          bordered
          scroll={{ x: true }}
          rowKey="_id"
        />
      </Card>

      {/* Lead Details Drawer */}
      <Drawer
        title="Lead Details"
        width={1000}
        placement="right"
        closable={true}
        onClose={handleCloseDetails}
        visible={isDetailsDrawerVisible}
        bodyStyle={{ padding: 0 }}
        headerStyle={{ padding: '16px 24px', borderBottom: '1px solid #f0f0f0' }}
        extra={
          !isEditing ? (
            <Button
              icon={<EditOutlined />}
              onClick={() => {
                form.setFieldsValue({
                  full_name: currentLead?.full_name,
                  phone_number: currentLead?.phone_number,
                  alternative_number: currentLead?.alternative_number,
                  e_mail: currentLead?.e_mail,
                  gender: currentLead?.gender,
                  city: currentLead?.city,
                  district: currentLead?.district,
                  state: currentLead?.state,
                  correct_pincode: currentLead?.correct_pincode,
                  teshil: currentLead?.teshil,
                  house_owned_rented: currentLead?.house_owned_rented,
                  product_category:currentLead?.product_category,
                  loan_amount_required: currentLead?.loan_amount_required,
                  employment_type: currentLead?.employment_type,
                  employment_category: currentLead?.employment_category,
                  monthly_income: currentLead?.monthly_income,
                  disposition: currentLead?.disposition,
                  customer_remarks: currentLead?.customer_remarks,
                  branch_remarks: currentLead?.branch_remarks,
                  business_remarks: currentLead?.business_remarks,
                  branch_to_assign: currentLead?.branch_to_assign || '',
                });
                setIsEditing(true);
              }}
              disabled={!currentLead}
            >
              Edit
            </Button>
          ) : (
            <Space>
              <Button
                icon={<CloseOutlined />}
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleUpdateLead}
                loading={loading}
              >
                Save
              </Button>
            </Space>
          )
        }
      >
        {currentLead ? (
          <div style={{ padding: '0 24px' }}>
            {isEditing ? (
              <EditFormContainer>
                <Form form={form} layout="vertical">
                  {/* Personal Information */}
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="full_name" label="Full Name">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="phone_number" label="Phone Number">
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="alternative_number" label="Alternative Number">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="e_mail" label="Email">
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="gender" label="Gender">
                        <Select>
                          <Option value="Male">Male</Option>
                          <Option value="Female">Female</Option>
                          <Option value="Other">Other</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      {/* Location Information */}
                      <Form.Item name="city" label="City">
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="district" label="District">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="state" label="State">
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="correct_pincode" label="Pincode">
                        <InputNumber style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="teshil" label="Teshil">
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="house_owned_rented" label="House Ownership">
                        <Input />
                      </Form.Item>
                    </Col>
                           <Col span={12}>
                     <Form.Item name="product_category" label="Product Category">
                  <Select>
                    <Option value="Secure">Secure</Option>
                    <Option value="Unsecure">Unsecure</Option>
                    <Option value="Suvidha">Suvidha</Option>
                  </Select>
                </Form.Item>
                    </Col>
                    <Col span={12}>
                      {/* Loan Information */}
                      <Form.Item name="loan_amount_required" label="Loan Amount Required">
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="employment_type" label="Employment Type">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="employment_category" label="Employment Category">
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="monthly_income" label="Monthly Income">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      {/* Status and Remarks */}
                      <Form.Item name="disposition" label="Status">
                        <Select>
                          <Option value="Interested">Interested</Option>
                          <Option value="Not Interested">Not Interested</Option>
                          <Option value="Follow Up">Follow Up</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="customer_remarks" label="Customer Remarks">
                        <TextArea rows={2} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="branch_remarks" label="Branch Remarks">
                        <TextArea rows={2} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="business_remarks" label="Business Remarks">
                        <TextArea rows={2} />
                      </Form.Item>
                      <Form.Item name="branch_to_assign" label="Select Branch">
                        <Select
                          showSearch
                          style={{ fontSize: '18px', fontWeight: '600', color: 'black', borderRadius: '10px' }}
                          placeholder={<p style={{ fontSize: '18px', fontWeight: '600', borderRadius: '10px' }}>Select a branch</p>}
                          optionFilterProp="children"
                          onChange={(value) => setBranchToAssign(value)}
                          filterOption={(input, option) =>
                            option?.props?.children?.props?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          }
                        >
                          {Object.keys(branchData).map((branchKey) => (
                            <Select.Option key={branchKey} value={branchKey}>
                              <p style={{ fontSize: '18px', fontWeight: '600', color: 'black', borderRadius: '10px' }}>{branchKey}</p>
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
              </EditFormContainer>
            ) : (
              <>
                {/* Lead Header */}
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
                  <Avatar
                    size={64}
                    icon={<UserOutlined />}
                    style={{ backgroundColor: '#1890ff', marginRight: 16 }}
                  />
                  <div>
                    <Title level={4} style={{ marginBottom: 4 }}>
                      {currentLead.full_name || 'N/A'}
                    </Title>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                      <StatusTag
                        color={
                          currentLead.disposition === 'Interested' ? 'green' :
                            currentLead.disposition === 'Not Interested' ? 'red' : 'default'
                        }
                      >
                        {currentLead.disposition || 'Pending'}
                      </StatusTag>
                      <Text type="secondary" style={{ marginLeft: 8 }}>
                        <ClockCircleOutlined /> {moment(currentLead.uploaded_at).fromNow()}
                      </Text>
                    </div>
                    <Text copyable>{currentLead._id}</Text>
                  </div>
                </div>

                {/* Contact Information */}
                <DetailSection>
                  <DetailHeader>
                    <PhoneOutlined />
                    <h4>Contact Information</h4>
                  </DetailHeader>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Phone">
                      <ContactItem>
                        <PhoneOutlined />
                        {currentLead.phone_number || 'N/A'}
                      </ContactItem>
                    </Descriptions.Item>
                    <Descriptions.Item label="Email">
                      <ContactItem>
                        <MailOutlined />
                        {currentLead.e_mail || 'N/A'}
                      </ContactItem>
                    </Descriptions.Item>
                    <Descriptions.Item label="Alternative Number">
                      {currentLead.alternative_number || 'N/A'}
                    </Descriptions.Item>
                  </Descriptions>
                </DetailSection>

                <Divider style={{ margin: '16px 0' }} />

                {/* Location Details */}
                <DetailSection>
                  <DetailHeader>
                    <EnvironmentOutlined />
                    <h4>Location Details</h4>
                  </DetailHeader>
                  <Descriptions column={2} size="small">
                    <Descriptions.Item label="City">{currentLead.city || 'N/A'}</Descriptions.Item>
                    <Descriptions.Item label="District">{currentLead.district || 'N/A'}</Descriptions.Item>
                    <Descriptions.Item label="State">{currentLead.state || 'N/A'}</Descriptions.Item>
                    <Descriptions.Item label="Pincode">{currentLead.correct_pincode || 'N/A'}</Descriptions.Item>
                    <Descriptions.Item label="Teshil">{currentLead.teshil || 'N/A'}</Descriptions.Item>
                    <Descriptions.Item label="House Ownership">
                      <HomeOutlined /> {currentLead.house_owned_rented || 'N/A'}
                    </Descriptions.Item>
                  </Descriptions>
                </DetailSection>

                <Divider style={{ margin: '16px 0' }} />

                {/* Loan Details */}
                <DetailSection>
                  <DetailHeader>
                    <DollarOutlined />
                    <h4>Loan Details</h4>
                  </DetailHeader>
                  <Descriptions column={2} size="small">
                    <Descriptions.Item label="Product Category">
                      {currentLead.product_category || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Amount Required">
                      {currentLead.loan_amount_required || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Employment Type">
                      {currentLead.employment_type || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Employment Category">
                      {currentLead.employment_category || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Monthly Income">
                      {currentLead.monthly_income || 'N/A'}
                    </Descriptions.Item>
                  </Descriptions>
                </DetailSection>

                <Divider style={{ margin: '16px 0' }} />

                {/* Additional Information */}
                <DetailSection>
                  <DetailHeader>
                    <InfoCircleOutlined />
                    <h4>Additional Information</h4>
                  </DetailHeader>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Gender">
                      {currentLead.gender || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Allocated To">
                      {currentLead.allocated || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Branch">
                      <BankOutlined /> {currentLead.branch || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Customer Remarks">
                      {currentLead.customer_remarks || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Branch Remarks">
                      {currentLead.branch_remarks || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Business Remarks">
                      {currentLead.business_remarks || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Branch to Assign">
                      {currentLead.branch_to_assign || 'N/A'}
                    </Descriptions.Item>
                  </Descriptions>
                </DetailSection>
              </>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '24px' }}>
            <Spin tip="Loading lead details..." />
          </div>
        )
        }
      </Drawer >
    </DigitalLeadsContainer >
  );
};

export default DigitalLeadsScreen;